import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, XCircle, Loader2, Plug, ExternalLink, Trash2, FlaskConical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { useConnections, useCreateConnection, useDisconnect, useTestConnection } from '@/hooks/useConnections'
import { connectionsApi, type AppConnection } from '@/lib/connections-api'

interface ProviderInfo {
  id: 'hubspot' | 'servicenow' | 'salesforce'
  name: string
  description: string
  color: string
  authType: 'both' | 'credentials'
}

const PROVIDERS: ProviderInfo[] = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Create tickets in HubSpot CRM when service requests complete.',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    authType: 'both',
  },
  {
    id: 'servicenow',
    name: 'ServiceNow',
    description: 'Create incidents in ServiceNow via the Table API.',
    color: 'bg-green-100 text-green-800 border-green-200',
    authType: 'credentials',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Create cases in Salesforce via the REST API.',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    authType: 'credentials',
  },
]

export function ConnectionsPage() {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const { data: connections, isLoading } = useConnections()
  const disconnect = useDisconnect()
  const testConnection = useTestConnection()
  const [connectDialogProvider, setConnectDialogProvider] = useState<ProviderInfo | null>(null)

  // Handle OAuth callback redirect
  useEffect(() => {
    const provider = searchParams.get('provider')
    const status = searchParams.get('status')
    if (provider && status) {
      if (status === 'success') {
        toast({ title: `${provider} connected`, description: 'Your account has been linked successfully.' })
      } else {
        const message = searchParams.get('message') || 'Unknown error'
        toast({ title: `${provider} connection failed`, description: message, variant: 'destructive' })
      }
    }
  }, [searchParams, toast])

  const getConnection = (providerId: string): AppConnection | undefined =>
    connections?.find((c) => c.provider === providerId)

  const handleConnect = (provider: ProviderInfo) => {
    setConnectDialogProvider(provider)
  }

  const handleDisconnect = async (connection: AppConnection) => {
    if (!confirm(`Disconnect ${connection.label}? Any SRO configs using this connection will need to be updated.`)) return
    try {
      await disconnect.mutateAsync(connection.id)
      toast({ title: 'Disconnected', description: `${connection.label} has been disconnected.` })
    } catch {
      toast({ title: 'Failed to disconnect', variant: 'destructive' })
    }
  }

  const handleTest = async (connection: AppConnection) => {
    try {
      const result = await testConnection.mutateAsync(connection.id)
      if (result.ok) {
        toast({ title: 'Connection OK', description: result.message })
      } else {
        toast({ title: 'Connection failed', description: result.message, variant: 'destructive' })
      }
    } catch {
      toast({ title: 'Test failed', variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">App Connections</h1>
        <p className="text-muted-foreground mt-1">
          Connect your external systems once. SRO configs will reference these connections.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROVIDERS.map((provider) => {
          const connection = getConnection(provider.id)
          const isConnected = connection?.status === 'connected'

          return (
            <Card key={provider.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plug className="h-5 w-5" />
                    {provider.name}
                  </CardTitle>
                  {isConnected ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      <XCircle className="h-3 w-3 mr-1" />
                      Not Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{provider.description}</p>

                {isConnected && connection && (
                  <div className="space-y-1 text-xs border rounded-md p-3 bg-muted/30">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Label:</span>
                      <span className="font-medium">{connection.label}</span>
                    </div>
                    {connection.connected_at && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Connected:</span>
                        <span>{new Date(connection.connected_at).toLocaleDateString()}</span>
                      </div>
                    )}
                    {connection.metadata && Object.keys(connection.metadata).length > 0 && (
                      <>
                        {connection.metadata.hub_domain && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Portal:</span>
                            <span>{String(connection.metadata.hub_domain)}</span>
                          </div>
                        )}
                        {connection.metadata.instance_url && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Instance:</span>
                            <span className="truncate max-w-[160px]">{String(connection.metadata.instance_url)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  {isConnected ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleTest(connection!)}
                        disabled={testConnection.isPending}
                      >
                        {testConnection.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <><FlaskConical className="h-4 w-4 mr-1" />Test</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleDisconnect(connection!)}
                        disabled={disconnect.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Disconnect
                      </Button>
                    </>
                  ) : provider.authType === 'both' ? (
                    <div className="flex gap-2 w-full">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => handleConnect(provider)}>
                        <Plug className="h-4 w-4 mr-1" />Private Token
                      </Button>
                      <Button size="sm" className="flex-1" onClick={() => { window.location.href = connectionsApi.getHubSpotAuthorizeUrl() }}>
                        <ExternalLink className="h-4 w-4 mr-1" />OAuth
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" className="w-full" onClick={() => handleConnect(provider)}>
                      <Plug className="h-4 w-4 mr-1" />Connect
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Credential Connect Dialog */}
      {connectDialogProvider && (
        <ConnectDialog
          provider={connectDialogProvider}
          open={!!connectDialogProvider}
          onOpenChange={(open) => !open && setConnectDialogProvider(null)}
        />
      )}
    </div>
  )
}


function ConnectDialog({
  provider,
  open,
  onOpenChange,
}: {
  provider: ProviderInfo
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { toast } = useToast()
  const createConnection = useCreateConnection()
  const [label, setLabel] = useState(provider.name)
  const [instanceUrl, setInstanceUrl] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [accessToken, setAccessToken] = useState('')

  const handleSubmit = async () => {
    const credentials: Record<string, string> = {}

    if (provider.id === 'hubspot') {
      if (!accessToken) {
        toast({ title: 'Private App token is required', variant: 'destructive' })
        return
      }
      credentials.access_token = accessToken
    } else if (provider.id === 'servicenow') {
      if (!instanceUrl || !username || !password) {
        toast({ title: 'All fields are required', variant: 'destructive' })
        return
      }
      credentials.instance_url = instanceUrl
      credentials.username = username
      credentials.password = password
    } else if (provider.id === 'salesforce') {
      if (!instanceUrl || !accessToken) {
        toast({ title: 'Instance URL and access token are required', variant: 'destructive' })
        return
      }
      credentials.instance_url = instanceUrl
      credentials.access_token = accessToken
    }

    try {
      await createConnection.mutateAsync({
        provider: provider.id,
        label,
        credentials,
      })
      toast({ title: `${provider.name} connected`, description: 'Connection saved successfully.' })
      onOpenChange(false)
    } catch {
      toast({ title: 'Connection failed', variant: 'destructive' })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {provider.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`My ${provider.name}`} />
          </div>
          {provider.id === 'hubspot' && (
            <div className="space-y-2">
              <Label>Private App Token</Label>
              <Input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="pat-na1-xxxxxxxx"
              />
              <p className="text-xs text-muted-foreground">
                Generate a Private App token in HubSpot &gt; Settings &gt; Integrations &gt; Private Apps.
                Grant it the <strong>Tickets</strong> scope.
              </p>
            </div>
          )}
          {(provider.id === 'servicenow' || provider.id === 'salesforce') && (
            <div className="space-y-2">
              <Label>Instance URL</Label>
              <Input
                value={instanceUrl}
                onChange={(e) => setInstanceUrl(e.target.value)}
                placeholder={
                  provider.id === 'servicenow'
                    ? 'https://dev12345.service-now.com'
                    : 'https://yourorg.my.salesforce.com'
                }
              />
            </div>
          )}
          {provider.id === 'servicenow' && (
            <>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </>
          )}
          {provider.id === 'salesforce' && (
            <div className="space-y-2">
              <Label>Access Token</Label>
              <Input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createConnection.isPending}>
            {createConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
